import React from "react";
import Menu from "@mui/material/Menu";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { useSeenPatientModal } from "@/modals/seenPatient";
import { usePatientHandoverModal } from "@/modals/patientHandover";
import { useChangePatientLocationModal } from "@/modals/changePatientLocation";
import { checkIfWeCanSendMessageForPatient, usePatientInstructionModal } from "@/modals/patientInstruction";
import { useAddObservationModal } from "@/modals/addObservation";
import { useAddUrinalysisModal } from "@/modals/addUrinalysis";
import { IconMenuItem } from "@/components/ContextMenu";
import { ArrivedIcon, DataTransferIcon, DismissIcon } from "@/assets/icons";
import { useDischardPatientModal } from "@/modals/dischargePatient";
import { useDismissPatientDistresstModal } from "@/modals/dismissPatientDistress";
import type { PatientEncounter } from "@/resources/types";
import IconButton from "@mui/material/IconButton";
import { getPatientRoute } from "@/pages/patient";
import { Icon } from "@/components/Icon";
import { SplitButton, SplitButtonOption } from "@/components/SplitButton";
import { generatePDF } from "../util/printable-obschart";
import ExportRecordButton from "./ExportRecordButton";
import { usePatientArrivedModal } from "@/modals/patientArrived";
import { useGenericAddPatientModal } from "@/modals/genericForm";
import { useAddExpectsPatientModal } from "@/modals/addExpectsPatient";
import { useConciergeMode } from "@/contexts/ConciergeModeContext";
import { useSettingsContext } from "@/pages/settings/SettingsProvider";

const usePatientEncounterActions = (encounter: PatientEncounter, onClick?: () => void) => {
  // actually uses encounter id, as the page is patients current encounter
  const patientRoute = getPatientRoute(encounter.id);
  const openSeenPatient = useSeenPatientModal();
  const onPatientArrived = usePatientArrivedModal();
  const openPatientHandover = usePatientHandoverModal();
  const openChangeLocation = useChangePatientLocationModal();
  const openPatientInstruction = usePatientInstructionModal();
  const openDismissDistress = useDismissPatientDistresstModal();
  const openDischargePatient = useDischardPatientModal();
  const openAddObservation = useAddObservationModal();
  const openAddUrinalysis = useAddUrinalysisModal();
  const openEditPatient = useGenericAddPatientModal();
  const openEditExpectsPatient = useAddExpectsPatientModal();
  const { isConciergeMode } = useConciergeMode();

  const onOpenModal = (callback: (props: any) => void, callbackProps?: any) => () => {
    callback({ encounter, ...(callbackProps || {}) });
    onClick?.();
  };

  return {
    patientRoute,
    onSeenPatient: onOpenModal(openSeenPatient),
    onPatientArrived: onOpenModal(onPatientArrived),
    onPatientHandover: onOpenModal(openPatientHandover),
    onChangeLocation: onOpenModal(openChangeLocation),
    onPatientInstruction: onOpenModal(openPatientInstruction),
    onDismissDistress: onOpenModal(openDismissDistress),
    onDischargePatient: onOpenModal(openDischargePatient),
    onAddObservation: onOpenModal(openAddObservation),
    onAddUrinalysis: onOpenModal(openAddUrinalysis),
    onEditPatient: encounter.status === "expected"
      ? onOpenModal(openEditExpectsPatient, { mode: "edit" })
      : onOpenModal(openEditPatient, { mode: "edit", configId: isConciergeMode ? "concierge" : undefined }),
  };
};

const PatientActions = React.memo(({ encounter }: { encounter: PatientEncounter; }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const close = React.useCallback(() => setAnchorEl(null), []);
  const actions = usePatientEncounterActions(encounter, close);

  const isExpects = encounter.status === "expected";
  const actionsActive = !["discharged", "disabled"].includes(encounter.status);
  const isDistressed = encounter.distressed;
  const isAllowSendMessage =
    actionsActive && checkIfWeCanSendMessageForPatient(encounter.patient);

  const onClick = React.useCallback<React.MouseEventHandler<HTMLButtonElement>>(e => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  }, [setAnchorEl]);

  return (
    <>
      <IconButton onClick={onClick}><Icon icon="menudots" /></IconButton>
      <Menu
        open={anchorEl !== null}
        onClose={close}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          style: { width: "11rem" },
        }}
      >
        <IconMenuItem
          hide={!isExpects}
          icon={<ArrivedIcon />}
          onClick={actions.onPatientArrived}
        >
          Arrived
        </IconMenuItem>
        <IconMenuItem
          disabled={!actionsActive || isExpects}
          icon="check"
          onClick={actions.onSeenPatient}
        >
          Seen
        </IconMenuItem>
        <IconMenuItem
          disabled={!actionsActive}
          icon="edit"
          onClick={actions.onEditPatient}
        >
          Edit patient
        </IconMenuItem>
        <IconMenuItem
          disabled
          icon={<DataTransferIcon />}
          onClick={actions.onPatientHandover}
        >
          Handover
        </IconMenuItem>
        <IconMenuItem
          disabled
          icon="location"
          onClick={actions.onChangeLocation}
        >
          Change location
        </IconMenuItem>
        <IconMenuItem
          disabled={!isAllowSendMessage}
          icon={<SendOutlinedIcon />}
          onClick={actions.onPatientInstruction}
        >
          Send message
        </IconMenuItem>
        <IconMenuItem
          icon="outcome"
          onClick={actions.onDischargePatient}
        >
          Discharge
        </IconMenuItem>

        {isDistressed && (
          <IconMenuItem
            icon={<DismissIcon />}
            onClick={actions.onDismissDistress}
          >
            Dismiss distress
          </IconMenuItem>
        )}
      </Menu>
    </>
  );
});

PatientActions.displayName = "patient/patient-actions-button";

export const PatientSplitActions = ({ encounter }: { encounter: PatientEncounter }) => {
  const actions = usePatientEncounterActions(encounter);

  const actionsActive = !["disabled", "discharged", "expected"].includes(encounter.status);
  const isAllowSendMessage =
    actionsActive && checkIfWeCanSendMessageForPatient(encounter.patient);

  const options = [
    // "Send message" makes the most sense as the default option
    {
      disabled: !isAllowSendMessage,
      icon: <SendOutlinedIcon />,
      label: "Send message",
      onClick: actions.onPatientInstruction,
    },
    {
      disabled: !actionsActive,
      icon: "check",
      label: "Seen",
      onClick: actions.onSeenPatient,
    },
    {
      disabled: true,
      icon: <DataTransferIcon />,
      label: "Handover",
      onClick: actions.onPatientHandover,
    },
    {
      disabled: true,
      icon: "location",
      label: "Change location",
      onClick: actions.onChangeLocation,
    },
    {
      disabled: true,
      icon: "outcome",
      label: "Discharge",
      onClick: actions.onDischargePatient,
    },
    encounter.distressed && {
      icon: <DismissIcon />,
      label: "Dismiss distress",
      onClick: actions.onDismissDistress,
    },
  ].filter(Boolean) as SplitButtonOption[];

  return (
    <SplitButton
      hideSelectedIndex
      options={options}
      matchButtonWidth
      PopperProps={{ placement: "bottom-end" }}
    />
  );
};

export const PatientObservationSplitActions = ({ encounter }: { encounter: PatientEncounter }) => {
  const actions = usePatientEncounterActions(encounter);

  const options = [
    { icon: "", label: "Add observations", onClick: actions.onAddObservation },
    { icon: "", label: "Add urinalysis", onClick: actions.onAddUrinalysis },
  ].filter(Boolean) as SplitButtonOption[];

  return (
    <SplitButton
      hideSelectedIndex
      matchButtonWidth
      disabled={["disabled", "discharged"].includes(encounter.status)}
      options={options}
      PopperProps={{ placement: "bottom-end" }}
    />
  );
};

export const SaveObservationsSplitActions = ({ encounter }: { encounter: PatientEncounter }) => {

  const encounterId = encounter.id;
  const [isBusy, setIsBusy] = React.useState(false);
  const csvExportBtnRef = React.useRef<HTMLAnchorElement | null>(null);
  const { settings } = useSettingsContext();

  const saveObservationsToPdf = (id: string, observationsOnly: boolean) => async () => {

    setIsBusy(true);
    await generatePDF(encounter as any, observationsOnly, settings);
    setIsBusy(false);
  };

  const saveObservationsToCsv = async () => {

    if (csvExportBtnRef.current) {
      // setIsBusy(true);
      csvExportBtnRef.current.click();
      // setIsBusy(false);
    }
  };

  const saveCompleteChart = saveObservationsToPdf(encounterId, false);
  const saveObsChartOnly = saveObservationsToPdf(encounterId, true);

  const options = [
    { icon: "", label: "Export to PDF", onClick: saveCompleteChart },
    { icon: "", label: "Export to PDF (Obs Only)", onClick: saveObsChartOnly },
    { icon: "", label: "Export to CSV", onClick: saveObservationsToCsv },
  ].filter(Boolean) as SplitButtonOption[];

  return (<>
    <SplitButton
      hideSelectedIndex
      matchButtonWidth
      // disabled={["disabled", "discharged"].includes(encounter.status)}
      options={options}
      PopperProps={{ placement: "bottom-end" }}
      variant="outlined"
      disabled={isBusy}
      loading={isBusy}
      sx={{ width: 215 }}
    />
    <ExportRecordButton
      encounter={encounter}
      hidden={true}
      ref={csvExportBtnRef}
    />
  </>);
};

export default PatientActions;
